import { AlertTriangle } from 'lucide-react'

import { cn } from '@/lib/utils'

export type ErrorCardProps = {
  title?: string
  message?: string
  error?: unknown
  onDismiss?: () => void
  dismissLabel?: string
  className?: string
}

function safeStringify(value: unknown, maxLength = 2000) {
  try {
    const seen = new WeakSet<object>()
    const json = JSON.stringify(
      value,
      (_key, v) => {
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v)) return '[Circular]'
          seen.add(v)
        }
        if (typeof v === 'string' && v.length > 500) {
          return `${v.slice(0, 500)}…`
        }
        return v
      },
      2,
    )

    if (typeof json !== 'string') return String(value)
    return json.length > maxLength ? `${json.slice(0, maxLength)}…` : json
  } catch {
    return String(value)
  }
}

function getErrorMessage(error: unknown): string | null {
  if (!error) return null
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message || error.name
  if (typeof error === 'object' && 'message' in error) {
    const message = (error as any).message
    return typeof message === 'string' ? message : null
  }
  return null
}

function getErrorTechnicalDetails(error: unknown): string | null {
  if (!error) return null

  if (error instanceof Error) {
    const base = {
      name: error.name,
      message: error.message,
      ...(error.cause ? { cause: error.cause } : null),
    }
    return safeStringify(base)
  }

  if (typeof error === 'object') {
    return safeStringify(error)
  }

  return String(error)
}

export function ErrorCard({
  title = 'Something went wrong',
  message,
  error,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
}: ErrorCardProps) {
  const derivedMessage = message ?? getErrorMessage(error)
  const technicalDetails = getErrorTechnicalDetails(error)

  return (
    <section
      role="alert"
      aria-live="polite"
      className={cn(
        'w-full rounded-lg border p-4',
        'border-red-200 bg-white text-slate-900',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-md bg-red-50">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <div className="text-sm font-semibold text-red-700">{title}</div>
            {derivedMessage ? (
              <div className="text-sm text-slate-700">{derivedMessage}</div>
            ) : null}
          </div>

          {technicalDetails ? (
            <details className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <summary className="cursor-pointer text-xs font-medium">
                Technical details
              </summary>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-slate-700">
                {technicalDetails}
              </pre>
            </details>
          ) : null}

          {onDismiss ? (
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                className="rounded-md border border-transparent px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                onClick={onDismiss}
              >
                {dismissLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}
